import React from "react";
import { motion } from "framer-motion";
import { Target, Eye, Heart, Award, Users, Rocket, ShieldCheck, Cpu } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import CTASection from "@/components/sections/CTASection";

const TIMELINE = [
  { year: "2017", title: "Fundação", desc: "Início das atividades com foco em suporte técnico corporativo." },
  { year: "2019", title: "Expansão de Infraestrutura", desc: "Equipe de redes e servidores com certificações Mikrotik e Linux." },
  { year: "2021", title: "Desenvolvimento de Sistemas", desc: "Lançamento da divisão de software, APIs e integrações." },
  { year: "2024", title: "Cloud & Automação", desc: "Migração de clientes para nuvem e implantação de RPA empresarial." },
  { year: "2026", title: "DeskcodeBR Premium", desc: "Atendimento 24/7 e novos serviços para clínicas, totens e BI." },
];

const DIFERENCIAIS = [
  { icon: ShieldCheck, title: "Segurança em primeiro lugar", desc: "Firewall, backup 3-2-1 e proteção contra ransomware em todos os projetos." },
  { icon: Rocket, title: "Performance e escala", desc: "Servidores otimizados, CI/CD e monitoramento contínuo." },
  { icon: Users, title: "Equipe certificada", desc: "Profissionais com experiência em Microsoft, Linux, Mikrotik e Cloud." },
  { icon: Cpu, title: "Tecnologia de ponta", desc: "Stack moderno: React, Node, Python, Docker, Kubernetes." },
];

export default function Sobre() {
  const { content } = useSite();
  return (
    <div data-testid="page-sobre">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 bg-gradient-to-br from-[#1A1858] to-[#2E2A8C] text-white relative overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-30"></div>
        <div className="dc-container relative">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-dc-accent">Sobre nós</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-3 mb-5 max-w-3xl leading-tight">
            Tecnologia que move empresas para o próximo nível
          </h1>
          <p className="text-base md:text-lg text-blue-100 max-w-3xl leading-relaxed">
            {content.about_text}
          </p>
        </div>
      </section>

      {/* Mission/Vision/Values */}
      <section className="section-padding bg-[#f5f7fa]">
        <div className="dc-container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-dc-primary">Nossa essência</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-dc-text mt-3">Missão, Visão e Valores</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card icon={Target} title="Missão" text={content.mission} />
            <Card icon={Eye} title="Visão" text={content.vision} />
            <Card icon={Heart} title="Valores" text={content.values} />
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-white">
        <div className="dc-container">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-dc-primary">Nossa jornada</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-dc-text mt-3">Linha do tempo da DeskcodeBR</h2>
          </div>

          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-dc-primary via-dc-accent to-dc-primary"></div>
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.year}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-12 mb-10 ${i % 2 ? "md:text-left" : "md:text-right"}`}
              >
                <div className="absolute left-2 md:left-1/2 md:-translate-x-1/2 top-2 w-5 h-5 rounded-full bg-dc-primary border-4 border-white shadow-md"></div>
                <div className={`${i % 2 ? "md:col-start-2" : "md:col-start-1"} bg-[#f5f7fa] rounded-xl p-6 border border-slate-100`}>
                  <div className="text-dc-primary font-heading font-bold text-2xl">{t.year}</div>
                  <h4 className="font-heading font-semibold text-lg text-dc-text mt-1">{t.title}</h4>
                  <p className="text-sm text-slate-600 mt-2">{t.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="section-padding bg-[#f5f7fa]">
        <div className="dc-container">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-dc-primary">Diferenciais</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-dc-text mt-3">Por que escolher a DeskcodeBR</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {DIFERENCIAIS.map((d, i) => (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-dc-primary/10 text-dc-primary flex items-center justify-center mb-5">
                  <d.icon className="w-6 h-6" strokeWidth={1.6} />
                </div>
                <h4 className="font-heading font-semibold text-lg text-dc-text mb-2">{d.title}</h4>
                <p className="text-sm text-slate-600">{d.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}

const Stat = ({ value, label }) => null;

const Card = ({ icon: Icon, title, text }) => (
  <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-slate-100">
    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-dc-primary to-dc-accent text-white flex items-center justify-center mb-5">
      <Icon className="w-6 h-6" strokeWidth={1.6} />
    </div>
    <h3 className="font-heading font-bold text-xl text-dc-text mb-3">{title}</h3>
    <p className="text-slate-600 leading-relaxed">{text}</p>
  </div>
);
